import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useGlobalContext } from "../../../Config.js";
import { ConfirmDialog } from "primereact/confirmdialog";
import Loader from "../../../shared/ui/Loader.js";
import { Toast } from 'primereact/toast';
import { ProductService } from "../../../services/ProductService.js";
import useEmpresas from "../hooks/useEmpresas.js";
import useTiposProducto from "../hooks/useTiposProducto.js";
import { tieneCaracteresInvalidos, normalizarTexto, validarDescripcion } from "../../../utilities/textValidations.js";
import { existeTipoProductoDuplicado } from "../utils/tipoProductoValidations.js";
import TableTipoProductos from "../components/TableTipoProductos.js";
import FormCrearTipoProducto from "../components/FormCrearTipoProducto.js";

const productService = new ProductService();

const MantenedorTipoProducto = () => {
  const toast = useRef(null);
  const idUsuario = sessionStorage.getItem("usuario_id");
  const { baseUrl, headerText } = useGlobalContext();
  const [descripcion, setDescripcion] = useState("");
  const [unidad, setUnidad] = useState("");
  const [tarifa, setTarifa] = useState(null);
  const [displayCreateDialog, setDisplayCreateDialog] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const { empresa, setEmpresa, listaEmpresas, nombreEmpresa } = useEmpresas(baseUrl);
  const { tipoProductos, loading, setLoading, fetchTipoProductos } = useTiposProducto(
    baseUrl,
    empresa,
    nombreEmpresa
  );

  const [unidades] = useState([
    { label: "Metro cuadrado (m²)", value: "m**2" },
  ]);

  useEffect(() => {
    if (empresa) {
      fetchTipoProductos();
    }
  }, [empresa, baseUrl]);

  const validateInputs = () => {
    // GUARD: descripcion obligatoria y largo maximo 45
    const errorDescripcion = validarDescripcion(descripcion);
    if (errorDescripcion) {
      alert(errorDescripcion);
      return false;
    }
    if (tieneCaracteresInvalidos(descripcion)) {
      alert("La descripción no debe contener caracteres especiales");
      return false;
    }
    if (!unidad || unidad.trim() === "") {
      alert("La unidad es obligatoria");
      return false;
    }
    if (tarifa === null || tarifa < 0) {
      alert("La tarifa debe ser un número positivo");
      return false;
    }
    if (!empresa) {
      alert("Debe seleccionar una empresa");
      return false;
    }
    return true;
  };


  const handleCreateTipoProducto = async () => {
    if (!validateInputs()) return;
    try {
      setLoading(true);

      if (!nombreEmpresa) {
        alert("Error: No se ha seleccionado una empresa");
        setLoading(false);
        return;
      }

      // Validación: no permitir crear tipos de producto con la misma descripción
      if (existeTipoProductoDuplicado(tipoProductos, descripcion)) {
        alert("Ya existe un tipo de producto con esa descripción.");
        setLoading(false);
        return;
      }

      // Normalización de datos
      const descNormalized = normalizarTexto(descripcion);

      await productService.createTipoProducto(
        baseUrl,
        {
          empresa: nombreEmpresa,
          descripcion: descNormalized,
          unidad,
          idUsuario,
          tarifa,
        },
        headerText
      );
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Tipo Producto creado exitosamente' });
      setDisplayCreateDialog(false);
      await fetchTipoProductos();

    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al crear el Tipo Producto' });
      console.error("Error detallado al crear el Tipo Producto:", error);
    }finally{
      setLoading(false);
    }
  };

  // Header for the table
  const renderHeader = () => (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
      <span className="p-input-icon-left" style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue ? globalFilterValue.toUpperCase() : ""}
          onChange={(e) =>
            setGlobalFilterValue(e.target.value.toUpperCase())
          }
          placeholder="Buscar por Descripción"
        />
      </span>
      <span className="p-input-icon-left ml-2" style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
        {tipoProductos?.length} tipos de {nombreEmpresa}
      </span>
    </div>
  );

  return (
    <div className="grid">
      <Toast ref={toast} />
      <div className="col-12">
        <div className="card">
          <h4>Gestión de Tipos Productos</h4>
          <div className="p-field">
            <label htmlFor="empresa">Seleccione Empresa: </label>
            <Dropdown
              id="empresa"
              value={empresa}
              onChange={(e) => {
                setEmpresa(e.value);
                setDescripcion("");
                setUnidad("");
                setTarifa(null);
              }}
              options={listaEmpresas}
              optionLabel="nombre"
              optionValue="id"
              placeholder="Seleccione Empresa"
              className="p-inputtext-sm"
              style={{ borderRadius: "4px" }}
            />
        </div>
      </div>
        {empresa && (
          <>
            {loading ? (
              <>
                <Loader />
              </>
            ) : (
              <>
                <div className="card">
                  <Button
                    label="Crear Tipo Producto"
                    icon="pi pi-plus"
                    onClick={() => {
                      setDescripcion("");
                      setUnidad("");
                      setTarifa(null);
                      setDisplayCreateDialog(true);
                    }}
                    className="p-button p-component p-button-rounded p-button-primary mb-3"
                    raised
                  />
                  <TableTipoProductos
                    tipoProductos={tipoProductos}
                    loading={loading}
                    header={renderHeader()}
                    globalFilterValue={globalFilterValue}
                    nombreEmpresa={nombreEmpresa}
                  />

                  <FormCrearTipoProducto
                    displayDialog={displayCreateDialog}
                    setDisplayDialog={setDisplayCreateDialog}
                    descripcion={descripcion}
                    setDescripcion={setDescripcion}
                    unidad={unidad}
                    setUnidad={setUnidad}
                    tarifa={tarifa}
                    setTarifa={setTarifa}
                    unidades={unidades}
                    nombreEmpresa={nombreEmpresa}
                    handleCreateTipoProducto={handleCreateTipoProducto}
                    loading={loading}
                  />
                </div>
              </>
            )}
          </>
        )}

        <ConfirmDialog />
      </div>
    </div>
  );
};

export default MantenedorTipoProducto;
